// Library Methods
import { useMemo } from "react";
import { useTranslation } from "react-i18next";

// Components
import Box from "@mui/material/Box";
import Divider from "@mui/material/Divider";
import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";
import TitleWithTooltip from "src/components/shared/text/TitleWithTooltip/TitleWithTooltip";
import ExamCardContainer from "../card-ui/ExamCardContainer/ExamCardContainer";
import ExamCardHeader from "../card-ui/ExamCardHeader/ExamCardHeader";
import ExamCardButton from "../card-ui/ExamCardButton/ExamCardButton";

// Types
import { SelectedExamCardType } from "src/services/exams/types";

// Constants
import {
  DEFAULT_ANISEIKONIA_CARD,
  DEFAULT_ANISEIKONIA_SETTINGS_PER_MERIDIAN,
  aniseikoniaSettingsPerMeridianOptions,
  aniseikoniaTargetStyleOptions,
} from "src/constants/enums";

// Utils
import { hasAniseikoniaMeridian } from "src/utils/exams/aniseikoniaCardHelper";

type AniseikoniaCardType = {
  label: string;
  selectedExamCard: SelectedExamCardType | undefined;
  updateExamCardProperty: (
    propertyName: keyof SelectedExamCardType,
    value: SelectedExamCardType[keyof SelectedExamCardType]
  ) => void;
  onRemoveCard: () => void;
};

const AniseikoniaCard = ({
  label,
  selectedExamCard,
  updateExamCardProperty,
  onRemoveCard,
}: AniseikoniaCardType) => {
  const { t } = useTranslation();

  const examCardInfo = selectedExamCard ?? DEFAULT_ANISEIKONIA_CARD;

  const {
    horizontal,
    vertical,
    overall,
    aniseikoniaTargetStyle,
    aniseikoniaSettingsPerMeridian,
  } = examCardInfo;

  const selectedTargetStyle =
    aniseikoniaTargetStyle ?? aniseikoniaTargetStyleOptions[0];

  const selectedSettings =
    aniseikoniaSettingsPerMeridian ?? DEFAULT_ANISEIKONIA_SETTINGS_PER_MERIDIAN;

  // The webservice refuses an exam with no meridian, and this is the only one of its four
  // refusals reachable by ordinary use -- so the card says so before the request goes out,
  // rather than letting the whole exam creation fail on it.
  const hasMeridian = hasAniseikoniaMeridian(examCardInfo);

  const targetStyleOptions = useMemo(
    () =>
      aniseikoniaTargetStyleOptions.map((option) => ({
        label: t(option),
        value: option,
      })),
    [t]
  );

  const settingsOptions = useMemo(
    () =>
      aniseikoniaSettingsPerMeridianOptions.map((option) => ({
        label: String(option),
        value: option,
      })),
    []
  );

  // The two duration levers multiply: the section is one presentation per setting per meridian,
  // and each is around twenty seconds. With Overall off the report still carries an overall
  // value, derived as the mean of the other two, so two meridians is a shorter order rather
  // than a worse one.
  const meridianToggles = useMemo(
    () => [
      {
        key: "horizontal" as const,
        title: t("Horizontal"),
        value: Boolean(horizontal),
      },
      {
        key: "vertical" as const,
        title: t("Vertical"),
        value: Boolean(vertical),
      },
      {
        key: "overall" as const,
        title: t("Overall"),
        value: Boolean(overall),
      },
    ],
    [t, horizontal, vertical, overall]
  );

  const booleanOptions = useMemo(
    () => [
      { value: true, label: t("On") },
      { value: false, label: t("Off") },
    ],
    [t]
  );

  const textStyle = {
    color: "#FFF",
    fontSize: "12px",
  };

  // Truncates to a single line with an ellipsis instead of wrapping onto a second line --
  // minWidth: 0 is needed alongside the usual overflow trio since Typography sits as a flex item
  // here (its default min-width: auto would otherwise keep it from shrinking below the text's
  // full width).
  const titleStyle = {
    ...textStyle,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    minWidth: 0,
  };

  // A button only needs to wrap to a new line once its own label can't fit -- not at some
  // arbitrary shared width. "ch" scales with character count, so each button's floor tracks its
  // own text; +2ch covers the button's horizontal padding/border.
  const buttonMinWidth = (buttonLabel: string) => `${buttonLabel.length + 2}ch`;

  return (
    <ExamCardContainer>
      <ExamCardHeader isNew label={label} onRemoveCard={onRemoveCard} />
      <Grid
        item
        container
        display="flex"
        flexDirection="column"
        justifyContent="space-between"
        gap="8px"
        flexGrow={1}
      >
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "repeat(2, 1fr)",
            gap: "8px",
          }}
        >
          {/* Target Style */}
          <Grid item display="flex" flexDirection="column" gap="4px">
            <TitleWithTooltip
              title={t("Target Style")}
              tooltip={t(
                "Controls the shape the patient compares the two halves of."
              )}
              titleStyle={titleStyle}
              tooltipStyle={textStyle}
            />
            <Grid container item display="flex" flexWrap="wrap" gap="4px">
              {targetStyleOptions.map((option) => (
                <Grid
                  item
                  xs
                  fontSize="12px"
                  minWidth={buttonMinWidth(option.label)}
                  key={option.value}
                >
                  <ExamCardButton
                    isSelected={selectedTargetStyle === option.value}
                    updateValue={() =>
                      updateExamCardProperty(
                        "aniseikoniaTargetStyle",
                        option.value
                      )
                    }
                    label={option.label}
                  />
                </Grid>
              ))}
            </Grid>
          </Grid>

          {/* Matches per meridian */}
          <Grid item display="flex" flexDirection="column" gap="4px">
            <TitleWithTooltip
              title={t("Matches")}
              tooltip={t(
                "How many times the patient is asked to match the halves on each meridian. One is quickest. More than one is what lets the report carry a range around the number, check that the patient answers the same coming from either direction, and include the hidden control trials -- all of which are comparisons between matches, so one match has none of them."
              )}
              titleStyle={titleStyle}
              tooltipStyle={textStyle}
            />
            <Grid container item display="flex" flexWrap="wrap" gap="4px">
              {settingsOptions.map((option) => (
                <Grid
                  item
                  xs
                  fontSize="12px"
                  minWidth={buttonMinWidth(option.label)}
                  key={option.value}
                >
                  <ExamCardButton
                    isSelected={selectedSettings === option.value}
                    updateValue={() =>
                      updateExamCardProperty(
                        "aniseikoniaSettingsPerMeridian",
                        option.value
                      )
                    }
                    label={option.label}
                  />
                </Grid>
              ))}
            </Grid>
          </Grid>

          {/* Meridians */}
          {meridianToggles.map((meridian) => (
            <Grid
              item
              display="flex"
              flexDirection="column"
              gap="4px"
              key={meridian.key}
            >
              <TitleWithTooltip
                title={meridian.title}
                tooltip={t(
                  "Controls which meridians the patient is asked to match. At least one is required."
                )}
                titleStyle={titleStyle}
                tooltipStyle={textStyle}
              />

              <Grid container item display="flex" flexWrap="wrap" gap="4px">
                {booleanOptions.map((option) => (
                  <Grid
                    item
                    xs
                    fontSize="12px"
                    minWidth={buttonMinWidth(option.label)}
                    key={String(option.value)}
                  >
                    <ExamCardButton
                      isSelected={
                        option.value ? meridian.value : !meridian.value
                      }
                      updateValue={() =>
                        updateExamCardProperty(meridian.key, option.value)
                      }
                      label={option.label}
                    />
                  </Grid>
                ))}
              </Grid>
            </Grid>
          ))}
        </Box>

        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: "8px",
          }}
        >
          <Divider color="#5B6D7A" />

          {/* No optional toggles, for symmetrical reasons. Calibration is not read from the
              request and is always stored true: the section places its fusion lock at the angle
              the eye tracker says the patient fuses at, so it cannot measure anything without
              one. requiresTutorial is not read either and is always stored false -- the section's
              instructions are a stage of the section, shown against the lock the patient is about
              to use, so there is nothing for a toggle to turn on. */}
          {!hasMeridian && (
            <Typography fontSize="11px" color="#FF8A8A">
              {t("Aniseikonia sections must measure at least one meridian.")}
            </Typography>
          )}
        </Box>
      </Grid>
    </ExamCardContainer>
  );
};

export default AniseikoniaCard;
